import { useState } from "react";
import { useNavigate, useMatch } from "react-router-dom";
import {
  useBreakpoints,
  LoadingShim,
  LoadingSkeleton,
} from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import { catcat as cc } from "cerulean";
import { entities } from "byzantine";
import { useDeleteBulkPayment } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import { useCancelDualApproval } from "byzantine/src/dbbl/businessLogic/entities/dualApprovals";
import { useCancelNachaPassthroughSubmission } from "byzantine/src/dbbl/businessLogic/entities/nachaPassthrough";
import {
  useFetchPaginatedFileImports,
  useFormattedPaginatedFileImports,
  useFileImportsTotalCount,
} from "byzantine/src/dbbl/businessLogic/entities/fileImportActivity";
import type { FormattedFileImportItem } from "byzantine/src/dbbl/businessLogic/entities/fileImportActivity/types";
import PageNavigation from "../../page_navigation/PageNavigation";
import { SudoProvider } from "../../SudoContext";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import { EmptyState, SearchBar } from "../common";
import FileImportTable from "./FileImportTable";
import CancelFileImportDialog from "./CancelFileImportDialog";
import type { FileImportCancelTargetId, FileImportCancelType } from "./types";
import usePaymentActivityContainer from "../hooks";
import { FilterChips } from "../Filters/filterConfig";
import type { FilterChipsType } from "../Filters/filterConfig";
import FilterContainer from "../Filters/FilterContainer";
import {
  PAYMENT_ACTIVITY_ROUTE,
  getFileImportReverseRoute,
  getFileImportReverseFromDrawerRoute,
} from "../routes";
import BulkReverseFlow from "../Bulk/BulkReverseFlow";

const FileImportContainer = () => {
  const { m, l } = useBreakpoints();
  const { l10n } = useLocalization();
  const { ach_payments_collect: achPaymentsCollectFeature } = useUserFeatures();
  const navigate = useNavigate();
  const [cancelTargetId, setCancelTargetId] =
    useState<FileImportCancelTargetId | null>(null);
  const [cancelTargetType, setCancelTargetType] =
    useState<FileImportCancelType | null>(null);
  const { deleteBulkPayment } = useDeleteBulkPayment();
  const { cancelDualApproval } = useCancelDualApproval();
  const { cancelNachaPassthroughSubmission } =
    useCancelNachaPassthroughSubmission();

  const reverseFromTableMatch = useMatch(
    `${getFileImportReverseRoute(":reverseFileImportId")}/*`
  );
  const reverseFromDrawerMatch = useMatch(
    `${getFileImportReverseFromDrawerRoute(":fileImportId")}/*`
  );
  const reverseFileImportId =
    reverseFromTableMatch?.params.reverseFileImportId ??
    reverseFromDrawerMatch?.params.fileImportId ??
    null;
  const reverseFromDrawer = Boolean(reverseFromDrawerMatch);

  const {
    state,
    formattedPaginatedPayments,
    paymentsLoading,
    bootstrapLoading,
    pagination,
    handleSearchChange,
    handleReset,
    handleSetFilters,
    refetchCurrentPage,
  } = usePaymentActivityContainer({
    useFetchPaginated: useFetchPaginatedFileImports,
    useFormattedPaginated: useFormattedPaginatedFileImports,
    useTotal: useFileImportsTotalCount,
    useBootstrap: entities.fileImportPayments.bootstrap.useBootstrap,
  });

  const toggleCancelDialog = (
    uuid?: FileImportCancelTargetId,
    type?: FileImportCancelType
  ) => {
    setCancelTargetId(uuid ?? null);
    setCancelTargetType(type ?? null);
  };

  const handleCancelPayment = () => {
    if (!cancelTargetId) return;

    const runProps = {
      onSuccess: () => {
        refetchCurrentPage({ disableCaching: true });
        toggleCancelDialog();
      },
    };

    if (cancelTargetType === "ach") {
      deleteBulkPayment(cancelTargetId as API.BulkPaymentId, runProps);
    } else if (cancelTargetType === "wire") {
      cancelDualApproval(
        cancelTargetId as API.DualApprovalId,
        "WireImportBatch",
        runProps
      );
    } else if (cancelTargetType === "ach_passthrough") {
      // pass-through files cancel through their own endpoint, which also works
      // after approval, rather than through the approval request
      cancelNachaPassthroughSubmission(
        cancelTargetId as API.NachaPassthrough.SubmissionId,
        runProps
      );
    }
  };

  const renderTable = () => {
    if (paymentsLoading) return <LoadingShim isLoading />;
    if (
      (formattedPaginatedPayments as FormattedFileImportItem[]).length === 0
    ) {
      return <EmptyState state={state} handleReset={handleReset} />;
    }

    return (
      <div className={cc({ "margin--top--l": !(m || l) })}>
        <FileImportTable
          payments={formattedPaginatedPayments as FormattedFileImportItem[]}
          handleToggleCancelDialog={toggleCancelDialog}
        />
        <PageNavigation
          pagination={pagination}
          marginClasses="margin--all--l"
        />
      </div>
    );
  };

  const ALLOWED_FILTERS = [
    FilterChips.FILE_IMPORT_METHOD,
    FilterChips.DATE,
    FilterChips.BULK_STATUS,
    FilterChips.AMOUNT,
    FilterChips.ACCOUNT,
  ] as FilterChipsType[];

  const PARENT_SELECTABLE_FILTERS = [
    FilterChips.BULK_STATUS,
    FilterChips.AMOUNT,
    FilterChips.ACCOUNT,
  ] as FilterChipsType[];

  if (achPaymentsCollectFeature) {
    ALLOWED_FILTERS.push(FilterChips.ACTION);
    PARENT_SELECTABLE_FILTERS.push(FilterChips.ACTION);
  }

  return (
    <LoadingSkeleton isLoading={bootstrapLoading}>
      <div className="margin--top--xl">
        <SearchBar
          handleSearchChange={handleSearchChange}
          searchValue={state.searchValue}
          placeholder={l10n.getString(
            "payment-activity-search-file-import-label"
          )}
        />
      </div>
      <div>
        <FilterContainer
          setFilters={handleSetFilters}
          filters={state.filters}
          allowedFilters={ALLOWED_FILTERS}
          parentSelectableFilters={PARENT_SELECTABLE_FILTERS}
        />
      </div>
      {renderTable()}
      {cancelTargetId && (
        <CancelFileImportDialog
          isOpen={Boolean(cancelTargetId)}
          handleClose={() => toggleCancelDialog()}
          handleCancel={handleCancelPayment}
          isPassthrough={cancelTargetType === "ach_passthrough"}
        />
      )}
      {reverseFileImportId && (
        <SudoProvider
          basename={
            reverseFromDrawer
              ? getFileImportReverseFromDrawerRoute(reverseFileImportId)
              : getFileImportReverseRoute(reverseFileImportId)
          }
        >
          <BulkReverseFlow
            bulkPaymentId={reverseFileImportId}
            reverseRoute={
              reverseFromDrawer
                ? getFileImportReverseFromDrawerRoute(reverseFileImportId)
                : getFileImportReverseRoute(reverseFileImportId)
            }
            dismissRoute={
              reverseFromDrawer
                ? `/${PAYMENT_ACTIVITY_ROUTE}/file-import/${reverseFileImportId}`
                : `/${PAYMENT_ACTIVITY_ROUTE}/file-import`
            }
            onComplete={() => {
              navigate(`/${PAYMENT_ACTIVITY_ROUTE}/file-import`);
            }}
          />
        </SudoProvider>
      )}
    </LoadingSkeleton>
  );
};

export default FileImportContainer;
